import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Building2, Building, Briefcase, BookOpen, MapPin, Package, Calculator, ShieldCheck, Globe, Sparkles, CheckCircle2, Heart, Compass, Scale } from "lucide-react";

const heroImg = "https://images.unsplash.com/photo-1747081207735-35fa43fd7a1e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwyfHx6dXJpY2glMjBjaXR5c2NhcGV8ZW58MHx8fHwxNzc5Nzk4NTMyfDA&ixlib=rb-4.1.0&q=85";
const alpsImg = "https://images.pexels.com/photos/35309605/pexels-photo-35309605.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const teamImg = "https://images.unsplash.com/photo-1573164574572-cb89e39749b4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBvZmZpY2UlMjBtZWV0aW5nfGVufDB8fHx8MTc3OTc5ODUzMnww&ixlib=rb-4.1.0&q=85";
const dashImg = "https://images.pexels.com/photos/577210/pexels-photo-577210.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const services = [
  { icon: MapPin, title: "Firmenansiedlungen", desc: "Die Handels- und Gewerbefreiheit der Schweiz erlaubt es, ein Unternehmen zu gründen oder sich anzusiedeln – wir begleiten Sie." },
  { icon: Building2, title: "Gesellschaftsgründung", desc: "Der Schritt in die Selbständigkeit – GmbH oder AG, mit allem Drum und Dran aus einer Hand." },
  { icon: Briefcase, title: "Verwaltungsmandate", desc: "Unabhängige Verwaltungsräte für Ihre Gesellschaft, kompetent und mit Schweizer Präzision." },
  { icon: BookOpen, title: "Finanzbuchhaltung", desc: "Die Kunst der Balance zwischen Aufwand und Nutzen: gesetzeskonform, transparent, zukunftsfähig." },
  { icon: Calculator, title: "Steuerberatung", desc: "Steueroptimierung für die Schweiz – speziell zugeschnitten auf Kundinnen und Kunden aus dem DACH-Raum." },
  { icon: Package, title: "Vorratsgesellschaften", desc: "Sofortiger Start mit einer bestehenden GmbH oder AG. Name und Sitz individuell anpassbar." },
];

export default function HomePage() {
  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[90vh] flex items-center pt-20" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={heroImg} alt="Zürich Stadtansicht" className="w-full h-full object-cover" />
          <div className="absolute inset-0 mbw-hero-overlay" />
        </div>
        <div className="relative mbw-container py-24 md:py-32 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8 text-white mbw-fade-up">
            <div className="mbw-overline mb-5 !text-[#DC2626]">Treuhand · Steuern · Firmengründung Schweiz</div>
            <h1 className="font-heading text-5xl md:text-7xl lg:text-8xl font-bold leading-[0.95] tracking-tighter mb-6">
              Beratung <span className="text-[#DC2626]">für Sie.</span><br />
              Alles aus einer Hand <span className="italic font-light">vor Ort.</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-200 max-w-2xl mb-10 leading-relaxed">
              Wir begleiten Unternehmerinnen und Unternehmer aus dem DACH-Raum bei der Firmengründung,
              Steuerberatung und im Treuhandwesen in der Schweiz.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/vorratsgesellschaften" className="btn-primary" data-testid="hero-cta-vorrats">
                Vorratsgesellschaften ansehen <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/kontakt" className="btn-inverse" data-testid="hero-cta-kontakt">
                Erstgespräch vereinbaren
              </Link>
            </div>
          </div>
          <div className="lg:col-span-4 hidden lg:block mbw-fade-up" style={{ animationDelay: "0.2s" }}>
            <div className="bg-white/95 backdrop-blur-xl rounded-2xl p-6 shadow-2xl border border-white/20">
              <div className="flex items-center gap-2 text-[#991B1B] mb-3">
                <Sparkles className="w-4 h-4" />
                <span className="mbw-overline !text-[#DC2626]">Neu: KI-Erstauskunft</span>
              </div>
              <h3 className="font-heading text-xl font-semibold text-[#1A2433] mb-2">
                Erste Antworten – sofort.
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed mb-4">
                Unser KI-Assistent beantwortet rund um die Uhr Ihre Fragen zu GmbH/AG-Gründung,
                MWST und Treuhand in der Schweiz.
              </p>
              <div className="flex items-center gap-2 text-sm font-medium text-[#1A2433]">
                Klick rechts unten <ArrowRight className="w-4 h-4 rotate-45 text-[#991B1B]" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="bg-[#0F172A] text-slate-300 py-6 border-y border-white/5">
        <div className="mbw-container flex flex-wrap items-center justify-center gap-x-12 gap-y-3 text-sm">
          <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#DC2626]" /> Schweizer Treuhand</div>
          <div className="flex items-center gap-2"><Globe className="w-4 h-4 text-[#DC2626]" /> DACH-Spezialisierung</div>
          <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#DC2626]" /> Alles aus einer Hand</div>
          <div className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#DC2626]" /> Digitaler Kundenbereich</div>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="py-24 md:py-32" data-testid="services-section">
        <div className="mbw-container">
          <div className="max-w-3xl mb-16">
            <div className="mbw-overline mb-4">Mit uns erhalten Sie in diesen Bereichen</div>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter text-[#1A2433] leading-[1.05]">
              Unterstützung, die <span className="text-[#991B1B]">wirklich trägt.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s, i) => (
              <Link
                key={s.title}
                to="/leistungen"
                className="mbw-card p-8 group block"
                data-testid={`service-card-${i}`}
                style={{ animationDelay: `${i * 0.05}s` }}
              >
                <div className="w-12 h-12 rounded-lg bg-[#1A2433] flex items-center justify-center mb-5 group-hover:bg-[#991B1B] transition-colors">
                  <s.icon className="w-6 h-6 text-[#DC2626] group-hover:text-white" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-[#1A2433] mb-2">{s.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed mb-4">{s.desc}</p>
                <div className="flex items-center gap-1.5 text-sm font-medium text-[#1A2433] group-hover:text-[#991B1B] transition-colors">
                  Mehr erfahren <ArrowRight className="w-4 h-4" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FIRMENPHILOSOPHIE */}
      <section className="py-24 md:py-32 bg-slate-50" data-testid="philosophy-section">
        <div className="mbw-container">
          <div className="max-w-3xl mb-16">
            <div className="mbw-overline mb-4">Firmenphilosophie</div>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter text-[#1A2433] leading-[1.05]">
              Vertrauen ist <span className="text-[#991B1B]">unsere Währung.</span>
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed mt-6">
              Wir verstehen Treuhand nicht als Verwaltung von Zahlen, sondern als Begleitung
              von Menschen, die etwas aufbauen wollen. Daraus ergeben sich unsere vier Leitsätze.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Heart, n: "01", title: "Mensch vor Mandat", text: "Hinter jeder Bilanz steht eine Person mit Zielen, Sorgen und Ambitionen. Wir nehmen uns die Zeit zuzuhören – bevor wir beraten." },
              { icon: ShieldCheck, n: "02", title: "Schweizer Verlässlichkeit", text: "Diskretion, Sorgfalt und Termintreue sind selbstverständlich – nicht Marketing, sondern Anspruch an uns selbst." },
              { icon: Compass, n: "03", title: "Klarheit statt Komplexität", text: "Wir reduzieren Steuer- und Treuhandwesen auf das Wesentliche. Sie sollen verstehen, was wir tun – und warum." },
              { icon: Scale, n: "04", title: "Langfristig denken", text: "Wir suchen keine schnellen Optimierungen, sondern Strukturen, die in zehn Jahren noch tragen." },
            ].map((p) => (
              <div key={p.n} className="mbw-card p-7" data-testid={`philosophy-card-${p.n}`}>
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#991B1B] mb-3">{p.n}</div>
                <div className="w-11 h-11 rounded-lg bg-[#1A2433] flex items-center justify-center mb-4">
                  <p.icon className="w-5 h-5 text-[#DC2626]" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-[#1A2433] mb-2">{p.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SWISS TRUST BANNER */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img src={alpsImg} alt="Schweizer Alpen" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-white/90" />
        </div>
        <div className="relative mbw-container grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="mbw-overline mb-4">Standort Schweiz</div>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tighter text-[#1A2433] mb-6 leading-tight">
              Schweizer Stabilität, <br /> persönliche Betreuung.
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed mb-6">
              Wir bringen seit Jahren Unternehmen aus Deutschland, Österreich und Liechtenstein
              in die Schweiz. Diskret, präzise und mit dem Wissen, das beide Seiten der Grenze
              braucht.
            </p>
            <ul className="space-y-3">
              {["Steueroptimierung für DACH-Unternehmer","Sitz, Domiziladresse & Verwaltungsmandate","Digitales Mandantenportal mit AbaNinja-Anbindung","Persönlicher Ansprechpartner – kein Callcenter"].map((t) => (
                <li key={t} className="flex items-start gap-3 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-[#991B1B] mt-0.5 flex-shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="relative">
            <img src={teamImg} alt="MBW Team" className="rounded-2xl shadow-2xl w-full h-[480px] object-cover" />
            <div className="absolute -bottom-6 -left-6 bg-[#1A2433] text-white p-6 rounded-xl shadow-xl max-w-xs hidden md:block">
              <div className="text-3xl font-heading font-bold text-[#991B1B] mb-1">+41 52 551 01 40</div>
              <div className="text-sm text-slate-300">Direktanruf in unsere Kanzlei</div>
            </div>
          </div>
        </div>
      </section>

      {/* PORTAL CTA */}
      <section className="py-24 md:py-32 bg-[#1A2433] text-white">
        <div className="mbw-container grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="mbw-overline mb-4 !text-[#DC2626]">Kundenportal</div>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tighter mb-6 leading-tight">
              Ihr <span className="text-[#991B1B]">digitaler Treuhand-Schreibtisch.</span>
            </h2>
            <p className="text-lg text-slate-300 leading-relaxed mb-8">
              Onboarding-Formular, Kostenrechner, Angebote und – sobald angebunden –
              Direktverbindung in Ihre AbaNinja-Buchhaltung. Alles an einem Ort.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/register" className="btn-primary" data-testid="cta-register">
                Konto erstellen <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/login" className="btn-inverse" data-testid="cta-login">
                Login
              </Link>
            </div>
          </div>
          <div>
            <img src={dashImg} alt="Kundenportal Vorschau" className="rounded-2xl shadow-2xl border border-white/10 w-full h-[420px] object-cover" />
          </div>
        </div>
      </section>
    </div>
  );
}
